import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../components/InnerHero";
import {
  BookOpen,
  Calendar,
  Database,
  Globe2,
  Languages,
  LibraryBig,
  Monitor,
  Network,
  Sparkles,
  Users,
} from "lucide-react";

const Section = ({ id, title, icon: Icon, children, sub }) => (
  <section id={id} className="scroll-mt-32">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-5">
      <div className="min-w-0">
        <h3
          className="text-2xl md:text-3xl text-brand font-semibold flex items-start gap-2 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {Icon && <Icon size={22} className="mt-1 flex-shrink-0" />} <span>{title}</span>
        </h3>
        {sub && <p className="text-sm text-[#3a3a3a]/80 italic mt-1">{sub}</p>}
      </div>
    </div>
    {children}
  </section>
);

const highlights = [
  [BookOpen, "Volumes", "46,490+"],
  [LibraryBig, "Titles", "6,733"],
  [Users, "Reference Books", "8,000+"],
  [Monitor, "Digital Systems", "30"],
];

const missions = [
  "To provide comprehensive resources and services that support teaching, learning, and research activities.",
  "To augment and maintain updated collections for holistic and sustainable development.",
  // "To promote information literacy, lifelong learning, and knowledge sharing.",
];

const libraryHighlights = [
  "Collection of 46,490+ volumes comprising 6,733 titles.",
  "More than 8,000 reference books covering engineering, science, management, and allied disciplines.",
  "Subscription to national and international journals through the VTU Consortium.",
  "Member of DELNET and NDLI.",
  "Spacious and well-equipped reading halls.",
  "Investment of over Rs. 1.12 crore in books, journals, and learning resources.",
  "Lending Library Scheme providing students with up to 5 books per semester.",
  "Dedicated Digital Library and Language Laboratory facilities.",
  "High-speed internet-enabled learning environment.",
];

const eResources = [
  "E-Journals 12,498",
  "51,000 e-books",
  "Research databases and conference proceedings",
  "Open-access academic resources",
  "IEEE Blended Learning Platform",
];

const publisherResources = [
  {
    no: 1,
    publisher: "Elsevier Science Direct",
    resources: "405 e-Journals",
    url: "https://www.sciencedirect.com",
  },
  {
    no: 2,
    publisher: "Springer Nature",
    resources: "710 e-Journals",
    url: "https://link.springer.com",
  },
  {
    no: 3,
    publisher: "Taylor & Francis",
    resources: "260 e-Journals",
    url: "https://www.tandfonline.com/",
  },
  {
    no: 4,
    publisher: "Emerald Publishing",
    resources: "Management 212 e-Journals",
    url: "https://www.emerald.com/insight/",
  },
  {
    no: 5,
    publisher: "ICE",
    resources: "29 e-Journals",
    url: "https://www.emerald.com/insight/",
  },
  {
    no: 6,
    publisher: "EBSCO Engineering Suite",
    resources: "6100 e-Journals, 24015 e-Books, 80+ microcourses",
    url: "https://search.ebscohost.com",
  },
  {
    no: 7,
    publisher: "Magzter database",
    resources: "Magazines-3174 Newspapers-1260 Journals-4581 Others-2595",
    url: "https://library.magzter.com",
  },
  {
    no: 8,
    publisher: "PACKT",
    resources: "2300 e-Books",
    url: "https://learning.packt.com",
  },
  {
    no: 9,
    publisher: "New Age International",
    resources: "450 e-Books",
    url: "https://digital.elib4u.com/",
  },
  {
    no: 10,
    publisher: "Edsol Informatics",
    resources: "20 e-Books",
    url: "https://ebooks.edsolinformatics.com/",
  },
  {
    no: 11,
    publisher: "Quiklrn",
    resources: "Language Communication Lab unlimited users",
    url: "https://home.quiklrn.com",
  },
  {
    no: 12,
    publisher: "Quiklrn",
    resources: "185 e-Books access with smart mobile app",
    url: "https://home.quiklrn.com",
  },
  {
    no: 13,
    publisher: "IEEE Blended e-Learning",
    resources: "70 e-Learning Courses",
    url: "http://blendedlearning.ieee.org",
  },
  {
    no: 14,
    publisher: "PAT Technology",
    resources: "14 e-Learning Courses",
    url: "https://vtu.edutainer.in/",
  },
  {
    no: 15,
    publisher: "DrillBit",
    resources: "Plagiarism Detection Software",
    url: "https://www.drillbitplagiarism.com",
  },
  {
    no: 16,
    publisher: "MAP Systems",
    resources: "Technology Platform",
    url: "https://access.vtuconsortium.com",
  },
  {
    no: 17,
    publisher: "CELUS",
    resources: "Online Software for automated harvesting and analysis of COUNTER and non-COUNTER statistics",
    url: "https://www.celus.net/",
  },
];

// const publishers = [
//   "Elsevier ScienceDirect",
//   "Springer Nature",
//   "Emerald Insight",
//   "EBSCO Engineering Suite",
//   "Cambridge University Press",
//   "Cengage Learning",
//   "Mint Books",
//   "Knimbus Digital Library Platform",
//   "DrillBit Plagiarism Detection Software",
// ];

// const services = [
//   "Reference Service",
//   "Book Circulation Service",
//   "Referral Service",
//   "New Arrivals Display Service",
//   "Newspaper Clipping Service",
//   "Digital Library Access",
//   "Language Laboratory Facility",
//   "Inter-Library Loan Service",
//   "Photocopy Service",
//   "Spiral Binding",
//   "Lamination Service",
//   "Remote Access to E-Resources",
// ];

const languages = ["English"];

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-brand/15 p-5 h-full">
    <Icon size={22} className="text-brand" />
    <h3 className="text-brand text-xl font-semibold mt-3">{title}</h3>
    <div className="text-sm text-[#2a2a2a]/85 leading-relaxed mt-2">
      {children}
    </div>
  </div>
);

const TagList = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <span
        key={item}
        className="px-2.5 py-1 bg-brand/10 text-brand text-xs rounded-full"
      >
        {item}
      </span>
    ))}
  </div>
);

const navItems = [
  ["overview", "Overview"],
  // ["vision", "Vision & Mission"],
  // ["highlights", "Highlights"],
  ["infrastructure", "Infrastructure", "/library/infrastructure"],
  // ["digital", "Digital Resources"],
  // ["services", "Services"],
  ["commitment", "Commitment"],
];

const Library = () => (
  <main>
    <InnerHero
      title="Library & Information Centre"
      image="/Library/hero.jpg"
      breadcrumbs={[
        { label: "Campus Life", to: "/campus" },
        { label: "Library" },
      ]}
    />

    <section className="bg-surface-alt/60 border-b border-brand/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex gap-4 sm:gap-x-6 overflow-x-auto text-xs font-sans-ui font-semibold tracking-wider text-brand">
        {navItems.map(([id, label, to]) =>
          to ? (
            <Link key={id} to={to} className="uppercase hover:underline whitespace-nowrap">
              {label}
            </Link>
          ) : (
            <a key={id} href={`#${id}`} className="uppercase hover:underline whitespace-nowrap">
              {label}
            </a>
          )
        )}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-12">
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10 md:space-y-12 min-w-0">
          <Section
            id="overview"
            title="About the Library"
            sub="JNAN SUDHA - a knowledge hub for learning, research, and innovation"
          >
            <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
              KLS VDIT CENTRAL LIBRARY
            </span>
            <div className="vdit-prose mt-3">
              <p>
                The <strong>Library & Information Centre</strong> of <strong>KLS Vishwanathrao Deshpande
                  Institute of Technology (KLS VDIT)</strong>, <strong>Haliyal</strong>, is a vibrant knowledge
                hub dedicated to supporting the academic, research, and learning needs
                of students, faculty, researchers, and staff. Established in <strong>2004</strong>, the
                library has grown into a modern information center offering extensive
                print and digital resources, advanced learning facilities, and
                technology-enabled services.
              </p>
              <p>
                Housed in a separate building named <strong>JNAN SUDHA</strong>, the library provides a
                conducive environment for <strong>learning</strong>, <strong>innovation</strong>, and <strong>research</strong>. The
                facility spans <strong>655 sq. m.</strong> and follows an open-access system, enabling
                users to freely access library resources. All books are classified and
                organized according to the <strong>Dewey Decimal Classification (DDC) system</strong>
                for efficient retrieval.
              </p>
              <p>
                Inspired by the vision of creating lifelong learners, the library
                serves as a center of academic excellence and intellectual growth,
                supporting the institute's mission of quality technical education and
                research.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
              {highlights.map(([Icon, k, v]) => (
                <div key={k} className="bg-[#7a1d2c] p-4 border border-[#7a1d2c]">
                  <Icon size={20} className="text-white/90" />
                  <div
                    className="text-3xl text-white font-semibold mt-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {v}
                  </div>
                  <div className="text-xs uppercase text-white/80 font-sans-ui tracking-widest mt-1">
                    {k}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <section className="bg-white border border-brand/15 p-5">
            <div className="grid sm:grid-cols-[160px_1fr] gap-5 items-center">
              <div className="w-full max-w-[180px] sm:max-w-none aspect-[4/5] overflow-hidden bg-surface-alt">
                <img
                  src="/Library/Librariran.jpg"
                  alt="Mr. Sunilkumar Yaragatti"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
                  LIBRARIAN
                </span>
                <h3
                  className="text-2xl md:text-3xl text-brand font-semibold mt-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Mr. Sunilkumar Yaragatti
                </h3>
                <p className="text-sm text-[#2a2a2a]/80 font-sans-ui mt-1">
                  MLISc, M.Phil.
                </p>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-brand/10">
              <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
                LIBRARY STAFF
              </span>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {[
                  ["Mr. Satish Patil", "MLISc", "Assistant Librarian"],
                  ["Mr. Vinay Nadiger", "B.Com", "FDA"],
                ].map(([name, qualification, designation]) => (
                  <div key={name} className="bg-surface-alt/50 border border-brand/10 p-4">
                    <h4 className="text-brand font-semibold">{name}</h4>
                    <p className="text-sm text-[#2a2a2a]/80 mt-1">
                      {qualification}
                    </p>
                    <p className="text-sm text-[#2a2a2a]/85 mt-1">
                      {designation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <Section id="vision" title="Vision & Mission" icon={Globe2}>
            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard icon={Globe2} title="Vision">
                <p>
                  To provide a student friendly learning environment that facilitates dissemination of knowledge using modern tools
                </p>
              </InfoCard>
              <InfoCard icon={Network} title="Mission">
                <ul className="list-disc pl-5 space-y-2">
                  {missions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </InfoCard>
            </div>
          </Section>

          <Section id="highlights" title="Library Highlights" icon={LibraryBig}>
            <div className="bg-white border border-brand/15 p-5">
              <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2 pl-5 list-disc text-sm text-[#2a2a2a]/85 leading-relaxed">
                {libraryHighlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </Section>

          <Section id="digital" title="Digital Library & E-Resources" icon={Database}>
            <div className="grid lg:grid-cols-2 gap-4">
              <InfoCard icon={Database} title="Digital Library">
                <p>
                  The Digital Library houses <strong>30 high-end multimedia systems</strong> with
                  high-speed internet connectivity, enabling access to e-journals,
                  e-books, databases, online courses, and multimedia learning
                  resources.
                </p>
                <p>
                  Through the <strong>VTU Consortium and institutional subscriptions</strong>, the library provides access to a vast collection of scholarly resources:
                </p>
                
                <a
                  href="https://access.vtuconsortium.com/klsvdit"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand font-semibold hover:underline break-all"
                >
                  https://access.vtuconsortium.com/klsvdit
                </a>
                <div className="mt-4">
                  <TagList items={eResources} />
                </div>
              </InfoCard>
              <InfoCard icon={Languages} title="Language Laboratory">
                <p>
                  The Language Laboratory supports communication skill development and
                  language learning through structured training.
                </p>
                <div className="mt-4">
                  <TagList items={languages} />
                </div>
              </InfoCard>
            </div>
          </Section>

          <Section id="services" title="Publishers" icon={BookOpen}>
            <div className="grid gap-3 md:hidden">
              {publisherResources.map((item) => (
                <article key={`${item.no}-${item.publisher}-card`} className="bg-white border border-brand/15 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center bg-brand/10 text-brand text-xs font-sans-ui font-semibold">
                      {item.no}
                    </span>
                    <div className="min-w-0">
                      <h4 className="text-brand font-semibold leading-snug">{item.publisher}</h4>
                      <p className="text-sm text-[#2a2a2a]/85 mt-1">{item.resources}</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 block text-sm text-brand underline-offset-2 hover:underline break-all"
                      >
                        {item.url}
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto bg-white border border-brand/15">
              <table className="w-full min-w-[760px] text-sm text-[#2a2a2a]">
                <thead className="bg-brand text-white font-sans-ui text-xs uppercase tracking-wider">
                  <tr>
                    <th className="w-14 px-3 py-3 text-left font-semibold">No.</th>
                    <th className="px-4 py-3 text-left font-semibold">Publisher</th>
                    <th className="px-4 py-3 text-left font-semibold">Resources</th>
                    <th className="px-4 py-3 text-left font-semibold">Website</th>
                  </tr>
                </thead>
                <tbody>
                  {publisherResources.map((item) => (
                    <tr key={`${item.no}-${item.publisher}`} className="odd:bg-surface-alt/40">
                      <td className="px-3 py-3 border-t border-brand/10 font-sans-ui text-[#3a3a3a]">
                        {item.no}
                      </td>
                      <td className="px-4 py-3 border-t border-brand/10 font-medium">
                        {item.publisher}
                      </td>
                      <td className="px-4 py-3 border-t border-brand/10">
                        {item.resources}
                      </td>
                      <td className="px-4 py-3 border-t border-brand/10">
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand underline-offset-2 hover:underline break-all"
                        >
                          {item.url}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="commitment" title="Commitment to Knowledge" icon={Sparkles}>
            <div className="bg-white border border-brand/15 p-5 vdit-prose">
              <p>
                The <strong>KLS VDIT Library</strong> continues to evolve as a modern learning resource
                center by <strong>integrating technology</strong>, <strong>digital resources</strong>, and <strong>quality
                  services</strong>. It plays a pivotal role in nurturing <strong>academic excellence</strong>, <strong>
                  fostering research culture</strong> and <strong>empowering students</strong> with the knowledge
                and skills required for professional success.
              </p>
            </div>
          </Section>
        </div>

        <aside className="space-y-5 min-w-0">
          <div className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36 self-start">
            <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
              Quick Facts
            </h4>
            <div className="mt-3 space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Calendar size={14} className="text-brand" />
                <span>Established: <strong>2004</strong></span>
              </div>
              <div className="flex items-start gap-2">
                <LibraryBig size={14} className="text-brand flex-shrink-0 mt-0.5" />
                <span>Building: <strong>JNAN SUDHA</strong></span>
              </div>
              <div>Area: <strong>655 sq. m.</strong></div>
              <div>Volumes: <strong>46,490+</strong></div>
              <div>Titles: <strong>6,733</strong></div>
              <div>Reference Books: <strong>8,000+</strong></div>
              <div>Digital Systems: <strong>30</strong></div>
              <div>Working Hours: <strong>8:00 AM to 5:00 PM</strong></div>
            </div>

            <div className="mt-6 pt-5 border-t border-brand/10">
              <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                Facilities
              </h4>
              <ul className="space-y-1.5 text-sm text-[#2a2a2a]">
                {[
                  "Open-access collection",
                  "Smart Reference Service- 6 Samsung Tablets Installed",
                  "Digital Library",
                  "Language Laboratory",
                  "Reading halls",
                  "Remote e-resource access",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-1.5">
                    <BookOpen size={12} className="text-brand flex-shrink-0 mt-1" />
                    <span className="min-w-0">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </section>
  </main>
);

export default Library;
